import { MarketDataCard } from '@src/components/MarketDataCard';
import { MOBILE_HEADER_TOOLBAR_PORTAL } from '@src/components/Mobile/MobileHeader/MobileHeader';
import {
  Box,
  ButtonVariants,
  CardViewEnum,
  DropdownItem,
  DropdownMenu,
  FeeModeEnum,
  Icon,
  IconButton,
  IconName,
  MarketDataCardState,
  MixpanelEvent,
  MixpanelEventProperty,
  Portal,
  Toggle,
  Tour,
  useMixpanel,
  useSecuritiesContext,
  type MarketDataCardConfig,
} from '@talos/kyoko';
import type { GroupviewPanelState } from 'dockview';
import { noop } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { v1 as uuid } from 'uuid';
import { MobileHeaderSpacer } from '../../../../components/Mobile/MobileHeader';
import { BOTTOM_SCROLL_GRADIENT_HEIGHT } from '../../../../components/Mobile/styles';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useDisplaySettings } from '../../../../providers/AppConfigProvider';
import { useMobileLayoutPanel } from '../../../../providers/MobileLayoutPanelProvider/MobileLayoutPanelContext';
import { MAX_NUM_COLUMNS } from '../../../../tokens/appconfig';
import { DELTA_1_BPS_TOGGLE, FEES_TOGGLE, FIRM_LIQUIDITY_TOGGLE } from '../../../../tokens/tooltips';
import { TourName } from '../../../../tokens/tours';
import type { MarketCardPanelConfig } from '../../../../types/LayoutConfig';
import { CardRow, CardsList, MobileCardPlaceholderWrapper, ScrollContainer } from './styles';

export interface MobileMarketCardsProps {
  panel: GroupviewPanelState;
  panelConfig: MarketCardPanelConfig;
}

export function MobileMarketCards({ panelConfig }: MobileMarketCardsProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pendingCardScrollIdRef = useRef<string | null>(null);
  const mixpanel = useMixpanel();
  const { hasBPSPricingSecurities } = useSecuritiesContext();
  const { enableDelta1BPSPricingMode } = useFeatureFlag();
  const {
    showAllInPrices,
    setShowAllInPrices,
    showFirmLiquidity,
    setShowFirmLiquidity,
    showDelta1BPS,
    setShowDelta1BPS,
  } = useDisplaySettings();
  const { updatePanelParams } = useMobileLayoutPanel();

  const cards = useMemo(() => (panelConfig.columns ?? []).flat(), [panelConfig.columns]);
  const defaultSubAccountId = panelConfig.defaults?.defaultSubAccountId;
  const showDelta1BPSToggle = hasBPSPricingSecurities && enableDelta1BPSPricingMode;

  const handleToggleAllInPrices = useCallback(
    (nextShowAllInPrices: boolean) => {
      const fees = nextShowAllInPrices ? FeeModeEnum.Taker : null;
      mixpanel.track(MixpanelEvent.ToggleFees, { [MixpanelEventProperty.Enabled]: fees });
      setShowAllInPrices(fees);
    },
    [mixpanel, setShowAllInPrices]
  );

  const handleToggleFirmLiquidity = useCallback(
    (nextShowFirmLiquidity: boolean) => {
      mixpanel.track(MixpanelEvent.ToggleFirmLiquidity, { [MixpanelEventProperty.Enabled]: nextShowFirmLiquidity });
      setShowFirmLiquidity(nextShowFirmLiquidity);
    },
    [mixpanel, setShowFirmLiquidity]
  );

  const handleToggleDelta1BPS = useCallback(
    (nextShowDelta1BPS: boolean) => {
      mixpanel.track(MixpanelEvent.ToggleDelta1BPS, { [MixpanelEventProperty.Enabled]: nextShowDelta1BPS });
      setShowDelta1BPS(nextShowDelta1BPS);
    },
    [mixpanel, setShowDelta1BPS]
  );

  const saveCard = useCallback(
    (nextCard: MarketDataCardConfig) => {
      updatePanelParams({
        columns: (panelConfig.columns ?? []).map(column =>
          column.map(card =>
            card.id === nextCard.id
              ? {
                  ...nextCard,
                  // Keep persisted view cross-platform; mobile-only compact is applied at render.
                  view: card.view,
                  state: MarketDataCardState.UPDATED_CARD,
                }
              : card
          )
        ),
      });
    },
    [panelConfig.columns, updatePanelParams]
  );

  const deleteCard = useCallback(
    (cardToDelete: MarketDataCardConfig) => {
      updatePanelParams({
        columns: (panelConfig.columns ?? []).map(column => column.filter(card => card.id !== cardToDelete.id)),
      });
    },
    [panelConfig.columns, updatePanelParams]
  );

  const addCard = useCallback(() => {
    const nextColumns = structuredClone(panelConfig.columns ?? [[]]);
    while (nextColumns.length < MAX_NUM_COLUMNS) {
      nextColumns.push([]);
    }
    const lastColumnWithContentIndex = nextColumns.reduce(
      (lastIndex, column, columnIndex) => (column.length > 0 ? columnIndex : lastIndex),
      -1
    );
    const firstEmptyColumnAfterContentIndex = nextColumns.findIndex(
      (column, columnIndex) => columnIndex > lastColumnWithContentIndex && column.length === 0
    );
    const targetColumnIndex =
      firstEmptyColumnAfterContentIndex >= 0 ? firstEmptyColumnAfterContentIndex : MAX_NUM_COLUMNS - 1;
    const nextCardId = uuid();
    nextColumns[targetColumnIndex].push({
      id: nextCardId,
      activeSlideIndex: 1,
      availableMarketAccounts: [],
      selectedMarketAccounts: [],
      selectedMarkets: [],
      availableMarkets: [],
      view: CardViewEnum.COMPACT,
      state: MarketDataCardState.NEW_CARD,
      topOfBookSizeBucket: '1',
      sizeBuckets: [],
      priceIncrement: '1',
    });
    pendingCardScrollIdRef.current = nextCardId;

    updatePanelParams({
      columns: nextColumns,
    });
  }, [panelConfig.columns, updatePanelParams]);

  // Wait for the card to be added to the DOM before scrolling to it
  useEffect(() => {
    const pendingCardScrollId = pendingCardScrollIdRef.current;
    if (!pendingCardScrollId || !scrollContainerRef.current) {
      return;
    }
    const cardRow = scrollContainerRef.current.querySelector<HTMLElement>(`[data-card-id="${pendingCardScrollId}"]`);
    if (!cardRow) {
      return;
    }
    cardRow.scrollIntoView({ block: 'start' });
    pendingCardScrollIdRef.current = null;
  }, [cards]);

  return (
    <ScrollContainer data-testid="mobile-market-cards-scroll" ref={scrollContainerRef}>
      <Tour tour={TourName.v70MarketCardUpdate} />
      <Portal portalId={MOBILE_HEADER_TOOLBAR_PORTAL}>
        <DropdownMenu
          triggerElement={
            <IconButton
              icon={IconName.Preferences}
              ghost
              round
              style={{ transform: 'rotate(90deg)' }}
              variant={ButtonVariants.Default}
              data-testid="mobile-market-cards-display-settings-button"
            />
          }
          trigger="click"
          placement="bottom-end"
          usePortal
          noPaddingAndBorder
          closeOnClickOutside
          dropdownProps={{ minWidth: '170px' }}
        >
          <DropdownItem
            onClick={() => handleToggleAllInPrices(!showAllInPrices)}
            suffix={
              <Toggle checked={!!showAllInPrices} onChange={handleToggleAllInPrices} tooltip={FEES_TOGGLE} label="" />
            }
          >
            Fees
          </DropdownItem>
          <DropdownItem
            onClick={() => handleToggleFirmLiquidity(!showFirmLiquidity)}
            suffix={
              <Toggle
                checked={showFirmLiquidity}
                onChange={handleToggleFirmLiquidity}
                tooltip={FIRM_LIQUIDITY_TOGGLE}
                label=""
              />
            }
          >
            Firm Liquidity
          </DropdownItem>
          {showDelta1BPSToggle && (
            <DropdownItem
              data-testid="delta-1-bps-toggle"
              onClick={() => handleToggleDelta1BPS(!showDelta1BPS)}
              suffix={
                <Toggle
                  checked={showDelta1BPS}
                  onChange={handleToggleDelta1BPS}
                  tooltip={DELTA_1_BPS_TOGGLE}
                  label=""
                />
              }
            >
              Delta-1 BPS
            </DropdownItem>
          )}
        </DropdownMenu>
      </Portal>
      <MobileHeaderSpacer data-testid="mobile-market-cards-header-spacer" />
      <CardsList>
        {cards.map(card => (
          <CardRow key={card.id} data-testid="mobile-market-card-row" data-card-id={card.id}>
            <MarketDataCard
              config={{ ...card, view: CardViewEnum.COMPACT }}
              isDraggable={false}
              isDragged={false}
              defaultSubAccountId={defaultSubAccountId}
              onDelete={deleteCard}
              onSave={saveCard}
              onHidePlaceholders={noop}
            />
          </CardRow>
        ))}
        <MobileCardPlaceholderWrapper data-testid="mobile-market-cards-add-button" onClick={addCard}>
          <Icon icon={IconName.Plus} size={32} />
        </MobileCardPlaceholderWrapper>
        <Box h={BOTTOM_SCROLL_GRADIENT_HEIGHT} />
      </CardsList>
    </ScrollContainer>
  );
}
